import React, { useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useConvertToAgreement } from "../../hooks/useConvertToAgreement";
import { ConvertToAgreementDialog } from "../../components/ConvertToAgreementDialog";
import { ReasonDialog } from "../../components/ReasonDialog";
import { Panel } from "@poseidon/ui/components/Panel";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import LoadingButton from "@mui/lab/LoadingButton";
import { usePermissions } from "@poseidon/ui/components";
import { useAuth } from "@poseidon/ui/components/AuthContext";
import { ApplicationHasAgreementWarning } from "../../components/ApplicationHasAgreementWarning";
import { isApplicationEditable } from "../../isApplicationEditable";
import config from "@poseidon/utils/config";
import * as api from "../../api";

export const DangerZone = ({ application }) => {
  const { canWrite } = usePermissions();
  const { user } = useAuth();
  const { closeReasonOptions } = config;
  const convertToAgreement = useConvertToAgreement(application.id);
  const readonly = !isApplicationEditable(application);
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const [convertOpen, setConvertOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  const handleConvertToAgreement = (options) => {
    convertToAgreement.convertToAgreement(options).then((success) => {
      setConvertOpen(false);
      if (success) {
        setToast("Application has been converted to an Agreement");
      } else {
        setError("Error converting Application to an Agreement");
      }
    });
  };

  const handleCloseApplication = async ({ reason, detail }) => {
    setChangingStatus(true);
    try {
      await api.closeApplication(application.id, {
        closeReason: reason,
        closeReasonDetail: detail,
        user,
      });
      setCloseOpen(false);
      setToast("Application closed");
    } catch (error) {
      setError(error.message);
    }
    setChangingStatus(false);
  };

  const handleReopenApplication = async () => {
    setChangingStatus(true);
    try {
      await api.reopenApplication(application.id);
      setToast("Application re-opened");
    } catch (error) {
      setError(error.message);
    }
    setChangingStatus(false);
  };

  return (
    <Panel expandable startCollapsed={true} title="Danger Zone">
      <ApplicationHasAgreementWarning
        application={application}
        sx={{ mb: 3 }}
      />
      <Typography variant="subtitle" color="textSecondary">
        Use these options with care
      </Typography>

      <Stack spacing={2} alignItems="start">
        {application.status === "active" && (
          <LoadingButton
            variant="contained"
            style={{ margin: "10px 0", minWidth: "200px" }}
            loading={changingStatus}
            disabled={!canWrite}
            onClick={() => setCloseOpen(true)}
          >
            Close application
          </LoadingButton>
        )}
        {application.status !== "active" &&
          application.status !== "agreement" && (
            <LoadingButton
              variant="contained"
              style={{ margin: "10px 0", minWidth: "200px" }}
              loading={changingStatus}
              disabled={!canWrite}
              onClick={handleReopenApplication}
            >
              Re-Open application
            </LoadingButton>
          )}
        <LoadingButton
          variant="contained"
          style={{ margin: "10px 0", minWidth: "200px" }}
          loading={
            convertToAgreement.saving ||
            (convertToAgreement.success && !readonly)
          }
          disabled={convertToAgreement.success || readonly || !canWrite}
          onClick={() => setConvertOpen(true)}
        >
          Convert to agreement
        </LoadingButton>
      </Stack>
      <ConvertToAgreementDialog
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        onConfirm={handleConvertToAgreement}
        application={application}
        saving={convertToAgreement.saving}
      />
      <ReasonDialog
        open={closeOpen}
        title="Close application"
        description="Choose the reason for closing. The application will no longer be editable until it is re-opened."
        options={closeReasonOptions}
        confirmLabel="Close application"
        onClose={() => setCloseOpen(false)}
        onConfirm={handleCloseApplication}
        saving={changingStatus}
      />
    </Panel>
  );
};
